// Shared vocabulary for the deterministic DSLabs simulator.
//
// This module contains no React code and no randomness. Every simulator event,
// node status, and state shape is defined here so the engine, the network
// model, and each protocol adapter agree on the same contracts.

export const EventKind = {
  MESSAGE: 'message',
  TIMER: 'timer',
  CLIENT_COMMAND: 'clientCommand',
  CRASH: 'crash',
  RESTART: 'restart',
  PARTITION: 'partition',
  HEAL: 'heal',
};

export const EventStatus = {
  PENDING: 'pending',
  DELIVERED: 'delivered',
  DROPPED: 'dropped',
};

export const NodeStatus = {
  UP: 'up',
  CRASHED: 'crashed',
};

// A SimEvent is a plain serializable object:
// {
//   id, kind, from, to, payload,
//   createdAt, deliverAt, status, parentId, label,
// }
// Timers carry payload { timerKey, timerType, ...extra } and fire at deliverAt.
// `from` is a node id, or 'client' for submitted commands, or 'system' for
// fault actions. `parentId` links duplicates/retries to their causal parent.

// A SimulationState is a plain serializable object:
// {
//   virtualTime, nextEventId, rngState,
//   nodes: { [nodeId]: { status, durable, volatile } },
//   events: SimEvent[],
//   network: { cuts: [{from,to}], dropRules: [{from,to}] },
//   config, violations: [{invariant, message, atTime}],
// }
// Protocol adapters own the shape of `durable`/`volatile` per node. The engine
// never interprets those fields except to retain or erase them on crash.

export function createInitialState(config) {
  return {
    virtualTime: 0,
    nextEventId: 1,
    rngState: null,
    nodes: {},
    events: [],
    network: { cuts: [], dropRules: [] },
    config,
    violations: [],
  };
}

export function nextEventId(state) {
  const id = 'e' + state.nextEventId;
  state.nextEventId += 1;
  return id;
}

// Build an event without an id; the engine assigns stable ids on enqueue so
// replay from a seed reproduces the identical id sequence.
export function createEvent({
  kind,
  from,
  to,
  payload = {},
  createdAt = 0,
  deliverAt = 0,
  parentId = null,
  label = '',
}) {
  return {
    id: null,
    kind,
    from,
    to,
    payload,
    createdAt,
    deliverAt,
    status: EventStatus.PENDING,
    parentId,
    label,
  };
}
